import type { MapColors } from "./MapColors";
import type { SceneCamp, SceneObjective } from "./MapScene";
import { circle, cross, diamond, ring } from "./shapes";

// Отметки карты целиком — так их рисуют и карта, и образцы легенды. Размеры — в единицах
// сцены (сторона карты — 1000), как в макете.
const CAMP_SIZE: Record<SceneCamp["kind"], number> = {
  weak: 32,
  medium: 38,
  strong: 44,
  vault: 42,
};
const OBJECTIVE_SIZE: Record<SceneObjective["kind"], number> = {
  walker: 36,
  guardian: 23,
  patron: 26,
};
const DEATH_SIZE = { me: 32, kill: 26, other: 18 } as const;
// Кружок героя: обводка, цвет команды, лицо.
const PLAYER_RADII = { me: [24.5, 22.5, 20], other: [17.5, 15, 13.5] } as const;

// Рисует в точке без поворота карты: иконки и лица остаются ровными.
export const upright = (
  context: CanvasRenderingContext2D,
  flipped: boolean,
  x: number,
  y: number,
  draw: () => void,
) => {
  context.save();
  context.translate(x, y);
  if (flipped) {
    context.scale(-1, -1);
  }
  draw();
  context.restore();
};

const face = (
  context: CanvasRenderingContext2D,
  flipped: boolean,
  image: CanvasImageSource | undefined,
  x: number,
  y: number,
  radius: number,
) => {
  if (image === undefined) {
    return;
  }
  upright(context, flipped, x, y, () => {
    context.beginPath();
    context.arc(0, 0, radius, 0, Math.PI * 2);
    context.clip();
    context.drawImage(image, -radius, -radius, radius * 2, radius * 2);
  });
};

// Лагерь: свободный на своей половине подсвечен, на половине врага — блёклый.
export const campMark = (
  context: CanvasRenderingContext2D,
  flipped: boolean,
  camp: Pick<SceneCamp, "x" | "y" | "kind" | "status">,
  icon: CanvasImageSource | undefined,
  colors: MapColors,
) => {
  if (camp.status === "missed") {
    context.globalAlpha = 0.35;
    circle(context, camp.x, camp.y, 32, colors.missed);
    context.globalAlpha = 1;
  }
  if (icon === undefined) {
    return;
  }
  context.globalAlpha = camp.status === "enemy" ? 0.55 : 1;
  upright(context, flipped, camp.x, camp.y, () => {
    const size = CAMP_SIZE[camp.kind];
    context.drawImage(icon, -size / 2, -size / 2, size, size);
  });
  context.globalAlpha = 1;
};

export const objectiveMark = (
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  kind: SceneObjective["kind"],
  color: string,
  colors: MapColors,
) => {
  diamond(
    context,
    x,
    y,
    OBJECTIVE_SIZE[kind],
    color,
    colors.ink,
    kind === "walker" ? "big" : kind === "patron" ? "ring" : "solid",
  );
};

export const sentryMark = (
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  color: string,
  colors: MapColors,
) => {
  diamond(context, x, y, 15, color, colors.ink, "ring");
};

export const shopMark = (
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  colors: MapColors,
) => {
  circle(context, x, y, 11, colors.ink);
  circle(context, x, y, 8, colors.muted);
};

export const runeMark = (
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  colors: MapColors,
) => {
  diamond(context, x, y, 30, colors.kill, colors.ink, "solid");
};

// Мид-босс; мёртвый — тусклый.
export const bossMark = (
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  alive: boolean,
  colors: MapColors,
) => {
  context.globalAlpha = alive ? 1 : 0.3;
  circle(context, x, y, 29, colors.ink);
  ring(context, x, y, 27, 3.5, colors.boss);
  circle(context, x, y, 12, colors.boss);
  context.globalAlpha = 1;
};

// Кристалл баффа мид-босса в цвете команды, которая его забрала.
export const buffMark = (
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  color: string,
  colors: MapColors,
) => {
  diamond(context, x, y, 24, color, colors.ink, "solid");
};

// Смерть: твоя — крупная, твоё убийство — средняя, остальные — мелкие.
export const deathMark = (
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  kind: keyof typeof DEATH_SIZE,
  color: string,
  colors: MapColors,
) => {
  cross(context, x, y, DEATH_SIZE[kind], color, colors.ink);
};

// Герой: лицо на круге цвета команды; ты — крупнее.
export const playerMark = (
  context: CanvasRenderingContext2D,
  flipped: boolean,
  x: number,
  y: number,
  kind: keyof typeof PLAYER_RADII,
  color: string,
  image: CanvasImageSource | undefined,
  colors: MapColors,
) => {
  const [outer, inner, photo] = PLAYER_RADII[kind];
  circle(context, x, y, outer, colors.ink);
  circle(context, x, y, inner, color);
  face(context, flipped, image, x, y, photo);
};

// Отрезок хвоста игрока: тоньше и прозрачнее к началу (`share` от 0 до 1).
export const trailSegment = (
  context: CanvasRenderingContext2D,
  from: readonly [number, number],
  to: readonly [number, number],
  share: number,
  opacity: number,
  colors: MapColors,
) => {
  // Концы отрезков встык: круглые перекрывались бы и рисовали хвост «бусами».
  context.lineCap = "butt";
  context.globalAlpha = opacity;
  context.lineWidth = 3 + 9 * share;
  context.strokeStyle = colors.kill;
  context.beginPath();
  context.moveTo(from[0], from[1]);
  context.lineTo(to[0], to[1]);
  context.stroke();
  context.globalAlpha = 1;
};

export const ZIPLINE_WIDTH = 5.5;
